import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { LogOut, Save, RotateCcw, ArrowLeft } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from '@/components/ui/alert-dialog';

interface ApplicationHeaderProps {
  onSaveAndExit: () => Promise<void>;
  onLogout: () => Promise<void>;
  onReset: () => Promise<void>;
  isLoading: boolean;
}

export const ApplicationHeader: React.FC<ApplicationHeaderProps> = ({
  onSaveAndExit,
  onLogout,
  onReset,
  isLoading
}) => {
  const { logout } = useAuth();

  const handleLogout = async () => {
    await onLogout();
    await logout();
  };

  return (
    <header className="w-full bg-white border-b">
      <div className="container mx-auto px-4 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/" className="flex items-center">
            <img 
              src="/lovable-uploads/d2d96d93-b158-4944-83f3-a8801b083f0c.png" 
              alt="The PEO App Logo" 
              className="h-10 w-auto" 
            />
          </Link>
          <Link to="/dashboard">
            <Button 
              variant="outline" 
              size="sm" 
              className="gap-1"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Dashboard
            </Button>
          </Link>
        </div>
        
        <div className="flex items-center gap-2">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                className="gap-2 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
              >
                <RotateCcw className="h-4 w-4" />
                <span className="hidden sm:inline">Reset</span>
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Reset Application</AlertDialogTitle>
                <AlertDialogDescription>
                  This will reset all your application data and start over from the beginning. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction 
                  onClick={onReset}
                  className="bg-red-600 text-white hover:bg-red-700"
                >
                  Reset Application
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <Button
            variant="outline"
            onClick={onSaveAndExit}
            className="gap-2"
            disabled={isLoading}
          >
            <Save className="h-4 w-4" />
            <span className="hidden sm:inline">Save & Exit</span>
          </Button>
          
          <Button
            variant="outline"
            onClick={handleLogout}
            className="gap-2"
          >
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline">Log Out</span>
          </Button>
        </div>
      </div>
    </header>
  );
};
